import { needsCategoryReview } from "shared/categorization/categorization";
import { formatCurrencyAmountOrMasked } from "shared/money/balances";
import { isBudgetImpactingTransaction } from "features/budget/components/transactions/budget-transactions/personalTransactionUtils";
import type { BudgetTransaction } from "types/BudgetTransaction";

function parseTxDate(tx: BudgetTransaction): Date {
    const raw = tx.date;
    const asNum = Number(raw);
    if (!isNaN(asNum) && asNum > 1e12) return new Date(asNum);
    return new Date(raw);
}

/** Budget month placement: installments use statement close, others use tx.date. */
export function getBudgetTransactionEffectiveDate(tx: BudgetTransaction): Date {
    const imp = tx.invoiceImport;
    if (imp?.installment && imp.statementDate) {
        const statementDate = formatImportDate(imp.statementDate);
        if (statementDate) {
            return new Date(`${statementDate}T12:00:00`);
        }
    }
    return parseTxDate(tx);
}

export function formatInstallmentPurchaseDate(tx: BudgetTransaction): string | undefined {
    const lineDate = formatImportDate(tx.invoiceImport?.lineDate);
    if (lineDate) return lineDate;
    if (!tx.invoiceImport?.installment) return undefined;
    const effective = formatImportDate(getBudgetTransactionEffectiveDate(tx));
    const raw = formatImportDate(tx.date);
    if (raw && effective && raw !== effective) return raw;
    return undefined;
}

export const TRANSACTION_GROUP_COLLAPSE_THRESHOLD = 5;
export const TRANSACTION_GROUP_EXPANDED_STORAGE_KEY = "budget.transactionGroup.expanded";

export interface BudgetTransactionGroupView {
    groupId: string;
    type: string;
    label: string;
    sourceId?: string;
    invoiceNumber?: string;
    statementDate?: string;
    dueDate?: string;
    transactions: BudgetTransaction[];
    totalsByCurrency: Record<string, number>;
    uncategorizedCount: number;
}

export interface BuildTransactionGroupsResult {
    standalone: BudgetTransaction[];
    groups: BudgetTransactionGroupView[];
}

export interface CardStatementSummary {
    importId: string;
    label: string;
    invoiceNumber?: string;
    statementDate?: string;
    dueDate: string;
    dueDateParsed: Date;
    totalsByCurrency: Record<string, number>;
    lineCount: number;
    isPaid: boolean;
}

export interface CardDueMarker {
    day: number;
    label: string;
    importId: string;
    totalsByCurrency: Record<string, number>;
}

export type MonthlyTimelineEntry =
    | { kind: "group"; sortDate: Date; group: BudgetTransactionGroupView }
    | { kind: "transaction"; sortDate: Date; transaction: BudgetTransaction };

export function isInMonth(date: Date, month: number, year: number): boolean {
    return date.getMonth() + 1 === month && date.getFullYear() === year;
}

export function getTransactionGroupKey(tx: BudgetTransaction): string | null {
    if (tx.transactionGroup?.id) return tx.transactionGroup.id;
    if (tx.invoiceImport?.importId) return tx.invoiceImport.importId;
    return null;
}

export function isGroupedTransaction(tx: BudgetTransaction): boolean {
    return Boolean(getTransactionGroupKey(tx));
}

function signedAmountForTotal(tx: BudgetTransaction): number {
    const amount = Number(tx.amount) || 0;
    return (tx.type ?? "expense") === "income" ? -amount : amount;
}

function formatImportDate(value: unknown): string | undefined {
    if (!value) return undefined;
    if (typeof value === "string") return value.slice(0, 10);
    if (value instanceof Date) return value.toISOString().slice(0, 10);
    return undefined;
}

function buildGroupMeta(transactions: BudgetTransaction[]) {
    const first = transactions[0];
    return {
        type: first.transactionGroup?.type || "card_summary_import",
        label: first.transactionGroup?.label
            || first.invoiceImport?.cardLabel
            || first.invoiceImport?.invoiceNumber
            || "Card summary",
        sourceId: first.transactionGroup?.sourceId || first.invoiceImport?.importId,
        invoiceNumber: first.invoiceImport?.invoiceNumber,
        statementDate: formatImportDate(first.invoiceImport?.statementDate),
        dueDate: formatImportDate(first.invoiceImport?.statementDueDate),
    };
}

function computeGroupTotals(transactions: BudgetTransaction[]) {
    const totalsByCurrency: Record<string, number> = {};
    let uncategorizedCount = 0;

    for (const tx of transactions) {
        if (!isBudgetImpactingTransaction(tx)) continue;
        const currency = (tx.currency || "ARS").toUpperCase();
        totalsByCurrency[currency] = (totalsByCurrency[currency] || 0) + signedAmountForTotal(tx);
        if (needsCategoryReview(tx)) uncategorizedCount += 1;
    }

    return { totalsByCurrency, uncategorizedCount };
}

export function filterTransactionsInMonth(
    transactions: BudgetTransaction[],
    month: number,
    year: number,
): BudgetTransaction[] {
    return transactions.filter((tx) => isInMonth(getBudgetTransactionEffectiveDate(tx), month, year));
}

export function sliceGroupForMonth(
    group: BudgetTransactionGroupView,
    month: number,
    year: number,
): BudgetTransactionGroupView | null {
    const transactions = filterTransactionsInMonth(group.transactions, month, year);
    if (transactions.length === 0) return null;

    const sorted = [...transactions].sort(
        (a, b) => getBudgetTransactionEffectiveDate(b).getTime() - getBudgetTransactionEffectiveDate(a).getTime(),
    );
    const { totalsByCurrency, uncategorizedCount } = computeGroupTotals(sorted);

    return {
        ...group,
        transactions: sorted,
        totalsByCurrency,
        uncategorizedCount,
    };
}

export function isImportStatementPaid(lines: BudgetTransaction[]): boolean {
    if (lines.length === 0) return false;
    if (lines.some((tx) => Boolean(tx.invoiceImport?.statementPayment?.paidAt))) {
        return true;
    }
    const purchaseLines = lines.filter((tx) => !tx.invoiceImport?.isStatementPayment);
    return purchaseLines.length > 0 && purchaseLines.every((tx) => Boolean(tx.liquidityPoolId));
}

export function getStatementPaymentPools(lines: BudgetTransaction[]): Record<string, string> {
    const statementPayment = lines.find((tx) => tx.invoiceImport?.statementPayment)?.invoiceImport?.statementPayment;
    if (statementPayment?.poolByCurrency) {
        return Object.fromEntries(
            Object.entries(statementPayment.poolByCurrency).map(([currency, poolId]) => [
                currency.toUpperCase(),
                poolId,
            ]),
        );
    }

    const legacy: Record<string, string> = {};
    for (const tx of lines) {
        if (tx.invoiceImport?.isStatementPayment) continue;
        const currency = (tx.currency || "ARS").toUpperCase();
        if (tx.liquidityPoolId && !legacy[currency]) {
            legacy[currency] = tx.liquidityPoolId;
        }
    }
    return legacy;
}

export function buildCardStatementSummaries(transactions: BudgetTransaction[]): CardStatementSummary[] {
    const importMap = new Map<string, BudgetTransaction[]>();

    for (const tx of transactions) {
        const importId = tx.invoiceImport?.importId;
        if (!importId || tx.invoiceImport?.isStatementPayment) continue;
        const bucket = importMap.get(importId) || [];
        bucket.push(tx);
        importMap.set(importId, bucket);
    }

    const summaries: CardStatementSummary[] = [];

    for (const [importId, lines] of importMap) {
        const dueDate = formatImportDate(lines[0]?.invoiceImport?.statementDueDate);
        if (!dueDate) continue;

        const meta = buildGroupMeta(lines);
        const { totalsByCurrency } = computeGroupTotals(lines);
        const isPaid = isImportStatementPaid(lines);

        summaries.push({
            importId,
            label: meta.label,
            invoiceNumber: meta.invoiceNumber,
            statementDate: meta.statementDate,
            dueDate,
            dueDateParsed: new Date(`${dueDate}T12:00:00`),
            totalsByCurrency,
            lineCount: lines.length,
            isPaid,
        });
    }

    return summaries.sort((a, b) => a.dueDateParsed.getTime() - b.dueDateParsed.getTime());
}

export function getImportTransactions(
    transactions: BudgetTransaction[],
    importId: string,
): BudgetTransaction[] {
    return transactions.filter(
        (tx) => tx.invoiceImport?.importId === importId && !tx.invoiceImport?.isStatementPayment,
    );
}

export function suggestPoolsForImport(
    lines: BudgetTransaction[],
): Record<string, string | undefined> {
    const result: Record<string, string | undefined> = {};
    for (const tx of lines) {
        const currency = (tx.currency || "ARS").toUpperCase();
        if (!result[currency] && tx.liquidityPoolId) {
            result[currency] = tx.liquidityPoolId;
        }
    }
    return result;
}

export function buildCardDueSummariesForDay(
    summaries: CardStatementSummary[],
    year: number,
    month: number,
    day: number,
): CardStatementSummary[] {
    return summaries.filter(
        (summary) =>
            summary.dueDateParsed.getFullYear() === year
            && summary.dueDateParsed.getMonth() + 1 === month
            && summary.dueDateParsed.getDate() === day,
    );
}

export function stripInvoiceFromLabel(label: string, invoiceNumber?: string): string {
    if (!invoiceNumber) return label;
    const suffix = ` · ${invoiceNumber}`;
    return label.endsWith(suffix) ? label.slice(0, -suffix.length) : label;
}

export function buildCardDueMarkersForMonth(
    summaries: CardStatementSummary[],
    year: number,
    month: number,
): CardDueMarker[] {
    return summaries
        .filter((summary) => !summary.isPaid && isInMonth(summary.dueDateParsed, month, year))
        .map((summary) => ({
            day: summary.dueDateParsed.getDate(),
            label: summary.label,
            importId: summary.importId,
            totalsByCurrency: summary.totalsByCurrency,
        }));
}

export function buildCardPaidMarkersForMonth(
    summaries: CardStatementSummary[],
    year: number,
    month: number,
): CardDueMarker[] {
    return summaries
        .filter((summary) => summary.isPaid && isInMonth(summary.dueDateParsed, month, year))
        .map((summary) => ({
            day: summary.dueDateParsed.getDate(),
            label: summary.label,
            importId: summary.importId,
            totalsByCurrency: summary.totalsByCurrency,
        }));
}

export function formatCardDueShort(isoDate: string, locale?: string): string {
    const [year, month, day] = isoDate.slice(0, 10).split("-");
    if (!year || !month || !day) return isoDate;
    const parsed = new Date(`${year}-${month}-${day}T12:00:00`);
    if (isNaN(parsed.getTime())) return `${day}/${month}`;
    return parsed.toLocaleDateString(locale, { day: "numeric", month: "short" });
}

export function formatStatementTotals(
    totalsByCurrency: Record<string, number>,
    balancesVisible: boolean,
    locale?: string,
): string {
    return Object.entries(totalsByCurrency)
        .map(([currency, amount]) => formatCurrencyAmountOrMasked(amount, currency, balancesVisible, locale))
        .join(" · ");
}

export function buildTransactionGroups(transactions: BudgetTransaction[]): BuildTransactionGroupsResult {
    const standalone: BudgetTransaction[] = [];
    const groupMap = new Map<string, BudgetTransaction[]>();

    for (const tx of transactions) {
        const groupId = getTransactionGroupKey(tx);
        if (!groupId) {
            standalone.push(tx);
            continue;
        }
        const bucket = groupMap.get(groupId) || [];
        bucket.push(tx);
        groupMap.set(groupId, bucket);
    }

    const groups = Array.from(groupMap.entries()).map(([groupId, groupTransactions]) => {
        const sorted = [...groupTransactions].sort(
            (a, b) => parseTxDate(b).getTime() - parseTxDate(a).getTime(),
        );
        const meta = buildGroupMeta(sorted);
        const { totalsByCurrency, uncategorizedCount } = computeGroupTotals(sorted);

        return {
            groupId,
            type: meta.type,
            label: meta.label,
            sourceId: meta.sourceId,
            invoiceNumber: meta.invoiceNumber,
            statementDate: meta.statementDate,
            dueDate: meta.dueDate,
            transactions: sorted,
            totalsByCurrency,
            uncategorizedCount,
        };
    });

    groups.sort((a, b) => {
        const aDate = a.statementDate || a.transactions[0]?.date || "";
        const bDate = b.statementDate || b.transactions[0]?.date || "";
        return bDate.localeCompare(aDate);
    });

    return { standalone, groups };
}

/** Statement close date, or latest line date when statement date is missing. */
export function getGroupAnchorDate(group: BudgetTransactionGroupView): Date {
    if (group.statementDate) {
        return new Date(`${group.statementDate}T12:00:00`);
    }
    if (group.transactions.length === 0) return new Date(0);
    return group.transactions.reduce((latest, tx) => {
        const d = parseTxDate(tx);
        return d.getTime() > latest.getTime() ? d : latest;
    }, parseTxDate(group.transactions[0]));
}

/** Latest purchase date in the viewed month. */
export function getGroupTimelineDate(
    group: BudgetTransactionGroupView,
    month: number,
    year: number,
): Date {
    let latestInMonth = new Date(0);
    for (const tx of group.transactions) {
        const d = getBudgetTransactionEffectiveDate(tx);
        if (isInMonth(d, month, year) && d.getTime() > latestInMonth.getTime()) {
            latestInMonth = d;
        }
    }
    return latestInMonth;
}

export function isGroupVisibleInMonth(
    group: BudgetTransactionGroupView,
    month: number,
    year: number,
): boolean {
    return group.transactions.some((tx) => isInMonth(getBudgetTransactionEffectiveDate(tx), month, year));
}

export function buildMonthlyTimeline(
    standalone: BudgetTransaction[],
    groups: BudgetTransactionGroupView[],
    month: number,
    year: number,
): MonthlyTimelineEntry[] {
    const entries: MonthlyTimelineEntry[] = [
        ...groups
            .filter((group) => group.transactions.length > 0)
            .map((group) => ({
                kind: "group" as const,
                sortDate: getGroupTimelineDate(group, month, year),
                group,
            })),
        ...standalone.map((transaction) => ({
            kind: "transaction" as const,
            sortDate: getBudgetTransactionEffectiveDate(transaction),
            transaction,
        })),
    ];

    entries.sort((a, b) => {
        const diff = b.sortDate.getTime() - a.sortDate.getTime();
        if (diff !== 0) return diff;
        if (a.kind !== b.kind) return a.kind === "group" ? -1 : 1;
        if (a.kind === "group" && b.kind === "group") {
            return a.group.groupId.localeCompare(b.group.groupId);
        }
        return 0;
    });

    return entries;
}

export function buildFilteredMonthView(
    transactions: BudgetTransaction[],
    month: number,
    year: number,
    selectedCategory: string,
): BuildTransactionGroupsResult & { timeline: MonthlyTimelineEntry[] } {
    const categoryFiltered = selectedCategory
        ? transactions.filter((tx) => tx.category === selectedCategory)
        : transactions;

    const { standalone: allStandalone, groups: allGroups } = buildTransactionGroups(categoryFiltered);

    const standalone = allStandalone.filter((tx) => isInMonth(getBudgetTransactionEffectiveDate(tx), month, year));
    const groups = allGroups
        .map((group) => sliceGroupForMonth(group, month, year))
        .filter((group): group is BudgetTransactionGroupView => group !== null);
    const timeline = buildMonthlyTimeline(standalone, groups, month, year);

    return { standalone, groups, timeline };
}

export function shouldDefaultGroupCollapsed(transactionCount: number): boolean {
    return transactionCount > TRANSACTION_GROUP_COLLAPSE_THRESHOLD;
}

export function readExpandedGroupIds(): Record<string, boolean> {
    try {
        const raw = localStorage.getItem(TRANSACTION_GROUP_EXPANDED_STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as Record<string, boolean>;
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
}

export function writeExpandedGroupId(groupId: string, expanded: boolean): void {
    const current = readExpandedGroupIds();
    if (expanded) {
        current[groupId] = true;
    } else {
        delete current[groupId];
    }
    localStorage.setItem(TRANSACTION_GROUP_EXPANDED_STORAGE_KEY, JSON.stringify(current));
}
